import { FC } from 'react'
import { Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { CUSTOMER } from 'src/app/_interfaces/customer.interface'
import { KTIcon } from 'src/marketConnect/helpers'
import { setSelectedCustomerForReport } from 'src/redux/slices/customerSlice'
import { useAppDispatch } from 'src/redux/storeHooks'

type Props = {
  customerDetail: CUSTOMER
  updateCustomerStatus: () => void
  onLoginAsCustomer: () => void
  onDeleteCustomer: () => void
  onSendEmail?: () => void
  onViewBillingHistory?: () => void
  updateCustomerStatusunlockAsync: () => void
  canModify?: boolean
  canViewReport?: boolean
  canView?: boolean
  canDeleteAccess?: boolean
  canViewBillingHistory?: boolean
  hideIndividualDelete?: boolean
}

const DROPDOWN_MENU_POPPER_CONFIG = {
  strategy: 'fixed' as const,
  modifiers: [
    {
      name: 'preventOverflow',
      options: {
        boundary: 'viewport',
        padding: 8,
      },
    },
    {
      name: 'flip',
      options: {
        fallbackPlacements: ['top', 'bottom', 'left', 'right'],
        padding: 8,
      },
    },
  ],
}

type ActionIconButtonProps = {
  iconName: string
  tooltip: string
  className?: string
  disabled?: boolean
  onClick?: () => void
}

const ActionIconButton: FC<ActionIconButtonProps> = ({
  iconName,
  tooltip,
  className = 'btn-light-primary',
  disabled = false,
  onClick,
}) => {
  const button = (
    <button
      type='button'
      className={`btn btn-icon btn-sm btn-light ${className}`}
      disabled={disabled}
      onClick={onClick}
      aria-label={tooltip}
    >
      <KTIcon iconName={iconName} className='fs-4' />
    </button>
  )

  return (
    <OverlayTrigger
      placement='top'
      delay={{ show: 250, hide: 100 }}
      overlay={<Tooltip id={`${iconName}-${tooltip}`}>{tooltip}</Tooltip>}
    >
      {disabled ? <span className='d-inline-flex'>{button}</span> : button}
    </OverlayTrigger>
  )
}

const UserActionsCell: FC<Props> = ({
  customerDetail,
  onLoginAsCustomer,
  updateCustomerStatus,
  onDeleteCustomer,
  onSendEmail,
  onViewBillingHistory,
  updateCustomerStatusunlockAsync,
  canModify,
  canViewReport,
  canView,
  canDeleteAccess,
  canViewBillingHistory,
  hideIndividualDelete = false,
}) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const allowModify = canModify !== undefined ? canModify : true
  const allowReport = canViewReport !== undefined ? canViewReport : true
  const allowView = canView !== undefined ? canView : true
  const allowBillingHistory = canViewBillingHistory !== undefined ? canViewBillingHistory : false
  const isActive = customerDetail.status === true
  const showIndividualDelete = Boolean(canDeleteAccess) && !hideIndividualDelete
  const hasOverflowActions =
    Boolean(onSendEmail) ||
    allowModify ||
    (Boolean(canDeleteAccess) && Boolean(customerDetail?.user_locked))

  const openReport = () => {
    dispatch(setSelectedCustomerForReport(customerDetail))
    navigate(`/reports/${customerDetail.id}`)
  }

  return (
    <div className='manage-customers-actions d-flex align-items-center gap-1'>
      {allowView ? (
        <ActionIconButton
          iconName='entrance-right'
          tooltip={isActive ? 'Login as customer' : 'Account inactive'}
          className='btn-active-light-primary'
          disabled={!isActive}
          onClick={onLoginAsCustomer}
        />
      ) : null}

      {allowReport ? (
        <ActionIconButton
          iconName='document'
          tooltip='View report'
          className='btn-active-light-success'
          onClick={openReport}
        />
      ) : null}

      {showIndividualDelete ? (
        <ActionIconButton
          iconName='trash'
          tooltip='Delete user'
          className='btn-active-light-danger'
          onClick={onDeleteCustomer}
        />
      ) : null}

      {hasOverflowActions ? (
        <Dropdown align='start'>
          <OverlayTrigger
            placement='top'
            delay={{ show: 250, hide: 100 }}
            overlay={<Tooltip id={`more-${customerDetail.id}`}>More actions</Tooltip>}
          >
            <Dropdown.Toggle
              as='button'
              type='button'
              className='btn btn-icon btn-sm btn-light btn-active-light-primary manage-customers-actions__more-toggle'
              aria-label='More actions'
            >
              <KTIcon iconName='dots-vertical' className='fs-4' />
            </Dropdown.Toggle>
          </OverlayTrigger>

          <Dropdown.Menu
            className='py-2 shadow-sm manage-customers-actions__menu'
            popperConfig={DROPDOWN_MENU_POPPER_CONFIG}
            renderOnMount
          >
            {onSendEmail ? (
              <Dropdown.Item className='fs-7 fw-semibold py-2' onClick={onSendEmail}>
                <KTIcon iconName='sms' className='fs-5 me-2 text-primary' />
                Send email
              </Dropdown.Item>
            ) : null}

            {allowModify ? (
              <Dropdown.Item className='fs-7 fw-semibold py-2' onClick={updateCustomerStatus}>
                <KTIcon iconName='cross-circle' className='fs-5 me-2 text-gray-600' />
                {isActive ? 'Deactivate' : 'Activate'}
              </Dropdown.Item>
            ) : null}

            {canDeleteAccess && customerDetail?.user_locked ? (
              <Dropdown.Item
                className='fs-7 fw-semibold py-2 text-success'
                onClick={updateCustomerStatusunlockAsync}
              >
                <KTIcon iconName='lock-2' className='fs-5 me-2 text-success' />
                Unlock user
              </Dropdown.Item>
            ) : null}

            {allowBillingHistory && onViewBillingHistory ? (
              <Dropdown.Item className='fs-7 fw-semibold py-2' onClick={onViewBillingHistory}>
                <KTIcon iconName='abstract-26' className='fs-5 me-2 text-info' />
                Billing history
              </Dropdown.Item>
            ) : null}
          </Dropdown.Menu>
        </Dropdown>
      ) : null}
    </div>
  )
}

export { UserActionsCell }
